import Vue from 'vue'
import * as Sentry from '@sentry/vue'

/**
 * MSAL `AccountInfo` usually does not carry `idToken`; the app stores it on `vm.msal.idToken`
 * and in `Vue.http.headers.common` (see plugins/msal.ts setAccount). Use those for retries.
 */
function applyAuthorizationToRequest(vm, request) {
  const token = vm.msal && vm.msal.idToken;
  if (token) {
    request.headers.set('Authorization', `Bearer ${token}`);
    return;
  }
  const common = Vue.http.headers.common['Authorization'];
  if (common) {
    request.headers.set('Authorization', common);
  }
}

/** MSAL PCA is `vm.msal.instance`; `vm.$msal` only exposes signIn / refreshToken (see plugins/msal.ts). */
function resolveActiveAccount(vm) {
  if (!vm || !vm.msal) {
    return null;
  }
  const { msal } = vm;
  if (msal.instance && typeof msal.instance.getActiveAccount === 'function') {
    const fromMsal = msal.instance.getActiveAccount();
    if (fromMsal) {
      return fromMsal;
    }
  }
  return msal.account || null;
}

export default {
  initialize: function () {
    // handle global api call error check and display here
    Vue.http.interceptors.push(function (request) {
      return function (response) {
        if (response.status === 401) {
          // Check if we have an active account
          const activeAccount = resolveActiveAccount(this);
          if (!activeAccount) {
            console.log('No active account, forcing re-authentication');
            return this.$msal.signIn()
              .then(() => {
                applyAuthorizationToRequest(this, request);
                return Vue.http(request);
              });
          }

          // Check if token is expired
          const tokenExp = activeAccount.idTokenClaims?.exp;
          if (tokenExp && tokenExp * 1000 <= Date.now()) {
            console.log('Token expired, forcing re-authentication');
            return this.$msal.signIn()
              .then(() => {
                applyAuthorizationToRequest(this, request);
                return Vue.http(request);
              });
          }

          // Try to refresh token
          return this.$msal.refreshToken()
            .then(() => {
              applyAuthorizationToRequest(this, request);
              return Vue.http(request);
            })
            .catch(error => {
              console.error('Token refresh failed:', error);
              return this.$msal.signIn()
                .then(() => {
                  applyAuthorizationToRequest(this, request);
                  return Vue.http(request);
                });
            });
        } else if (response.status >= 400) {
          let errorMessage = `An error occurred while executing request to url '${response.url}'` + '<br/>' +
            `Status code: ${response.status}\n` + '<br/>' +
            `Text: ${response.bodyText}`;
          Sentry.captureMessage(`vue-resource ${response.status} ${response.url}`, {
            level: 'error',
            tags: {
              http_status: String(response.status),
              url: response.url
            }
          });
          this.$root.apiError = errorMessage;
        }
        return response;
      }
    })
  }
}

